import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import { MobileLayout } from "@/components/MobileLayout";
import { FeatureGate } from "@/components/FeatureGate";
import { Dashboard } from "@/pages/Dashboard";
import { Proposals } from "@/pages/Proposals";
import { Events } from "@/pages/Events";
import { Books } from "@/pages/Books";
import { BookDisplays } from "@/pages/BookDisplays";
import { LiteracyStations } from "@/pages/LiteracyStations";
import { YearlyGoals } from "@/pages/YearlyGoals";
import { AIAssistant } from "@/pages/AIAssistant";
import { Login } from "@/pages/Login";
import { Register } from "@/pages/Register";
import { UserManagement } from "@/pages/UserManagement";
import { SupervisorDashboard } from "@/pages/SupervisorDashboard";
import { ShoutOuts } from "@/pages/ShoutOuts";
import { Inventory } from "@/pages/Inventory";
import { PrintQRCodes } from "@/pages/PrintQRCodes";
import { SupplyInventory } from "@/pages/SupplyInventory";
import { FeatureFlagsAdmin } from "@/pages/FeatureFlagsAdmin";
import { OfflineCheckout } from "@/pages/OfflineCheckout";
import Landing from "@/pages/Landing";
// Mobile pages
import { MobileHome } from "@/pages/mobile/MobileHome";
import { MobileEvents } from "@/pages/mobile/MobileEvents";
import { MobileBooks } from "@/pages/mobile/MobileBooks";
import { MobileShoutOuts } from "@/pages/mobile/MobileShoutOuts";
import { MobileOfflineCheckout } from "@/pages/mobile/MobileOfflineCheckout";
import { MobileInventory } from "@/pages/mobile/MobileInventory";

// Helper to check if user has a feature enabled
const hasFeature = (user, feature) => {
  if (user?.is_admin) return true;
  return user?.feature_flags?.[feature] === true;
};

// Feature-gated component wrapper
const FeatureRoute = ({ user, feature, label, children }) => {
  if (!hasFeature(user, feature)) {
    return <FeatureGate featureName={feature} featureLabel={label} />;
  }
  return children;
};

// Set up axios interceptor for auth
const token = localStorage.getItem("token");
if (token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  return isMobile;
};

// App content component (needs to be inside BrowserRouter for navigation)
const AppContent = ({ user, onLogout, viewMode, setViewMode, userLookup }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for stored view preference
  const [forceMobileView, setForceMobileView] = useState(() => {
    return localStorage.getItem("viewPreference") === "mobile";
  });
  const [forceFullView, setForceFullView] = useState(() => {
    return localStorage.getItem("viewPreference") === "full";
  });

  // Determine if we should show mobile view
  const showMobileView = (isMobile && !forceFullView) || forceMobileView;
  const isMobilePath = location.pathname.startsWith("/mobile");

  // Auto-redirect to mobile view if on mobile device and not forcing full view
  useEffect(() => {
    if (showMobileView && !isMobilePath && location.pathname !== "/login" && location.pathname !== "/register") {
      navigate("/mobile", { replace: true });
    }
  }, [showMobileView, isMobilePath, location.pathname, navigate]);

  const handleSwitchToMobile = () => {
    localStorage.setItem("viewPreference", "mobile");
    setForceMobileView(true);
    setForceFullView(false);
    navigate("/mobile");
  };

  const handleSwitchToFull = () => {
    localStorage.setItem("viewPreference", "full");
    setForceFullView(true);
    setForceMobileView(false);
    navigate("/");
  };

  // Mobile routes
  if (showMobileView || isMobilePath) {
    return (
      <MobileLayout user={user} onLogout={onLogout} onSwitchToFull={handleSwitchToFull}>
        <Routes>
          <Route path="/mobile" element={<MobileHome user={user} />} />
          <Route path="/mobile/events" element={<MobileEvents user={user} />} />
          <Route path="/mobile/books" element={<MobileBooks user={user} />} />
          <Route path="/mobile/shout-outs" element={<MobileShoutOuts user={user} />} />
          <Route path="/mobile/offline-checkout" element={<MobileOfflineCheckout user={user} />} />
          <Route path="/mobile/inventory" element={<MobileInventory user={user} />} />
          <Route path="*" element={<Navigate to="/mobile" replace />} />
        </Routes>
      </MobileLayout>
    );
  }

  // Full desktop routes
  return (
    <Layout 
      user={user} 
      onLogout={onLogout} 
      viewMode={viewMode} 
      setViewMode={setViewMode}
      onSwitchToMobile={handleSwitchToMobile}
      isMobile={isMobile}
    >
      <Routes>
        <Route path="/" element={<Dashboard viewMode={viewMode} />} />
        <Route path="/proposals" element={
          <FeatureRoute user={user} feature="proposals" label="Program Proposals">
            <Proposals user={user} viewMode={viewMode} userLookup={userLookup} />
          </FeatureRoute>
        } />
        <Route path="/events" element={
          <FeatureRoute user={user} feature="events" label="Events">
            <Events user={user} viewMode={viewMode} userLookup={userLookup} />
          </FeatureRoute>
        } />
        <Route path="/books" element={
          <FeatureRoute user={user} feature="books" label="Books">
            <Books user={user} viewMode={viewMode} userLookup={userLookup} />
          </FeatureRoute>
        } />
        <Route path="/book-displays" element={
          <FeatureRoute user={user} feature="book_displays" label="Book Displays">
            <BookDisplays user={user} viewMode={viewMode} userLookup={userLookup} />
          </FeatureRoute>
        } />
        <Route path="/literacy-stations" element={
          <FeatureRoute user={user} feature="literacy_stations" label="Literacy Stations">
            <LiteracyStations user={user} viewMode={viewMode} userLookup={userLookup} />
          </FeatureRoute>
        } />
        <Route path="/yearly-goals" element={
          <FeatureRoute user={user} feature="yearly_goals" label="Yearly Goals">
            <YearlyGoals viewMode={viewMode} userLookup={userLookup} />
          </FeatureRoute>
        } />
        <Route path="/shout-outs" element={
          <FeatureRoute user={user} feature="shout_outs" label="Shout Outs">
            <ShoutOuts user={user} />
          </FeatureRoute>
        } />
        <Route path="/inventory" element={
          <FeatureRoute user={user} feature="inventory" label="Inventory">
            <Inventory user={user} viewMode={viewMode} />
          </FeatureRoute>
        } />
        <Route path="/inventory/print-qr" element={
          <FeatureRoute user={user} feature="inventory" label="Inventory">
            <PrintQRCodes />
          </FeatureRoute>
        } />
        <Route path="/supplies" element={<Navigate to="/inventory" replace />} />
        <Route path="/ai-assistant" element={
          <FeatureRoute user={user} feature="ai_assistant" label="AI Assistant">
            <AIAssistant />
          </FeatureRoute>
        } />
        {(user.role === "supervisor" || user.is_admin) && (
          <Route path="/users" element={
            <FeatureRoute user={user} feature="user_management" label="User Management">
              <UserManagement />
            </FeatureRoute>
          } />
        )}
        {(user.role === "supervisor" || user.is_admin) && (
          <Route path="/supervisor" element={
            <FeatureRoute user={user} feature="supervisor_dashboard" label="Supervisor Dashboard">
              <SupervisorDashboard />
            </FeatureRoute>
          } />
        )}
        <Route path="/offline-checkout" element={
          <FeatureRoute user={user} feature="offline_checkout" label="Offline Checkout">
            <OfflineCheckout user={user} />
          </FeatureRoute>
        } />
        {user.is_admin && (
          <Route path="/admin/feature-flags" element={<FeatureFlagsAdmin />} />
        )}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/mobile/*" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("default"); // "default" uses role, "staff" forces staff view
  const [userLookup, setUserLookup] = useState({}); // Map of user IDs to names

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");
    
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  // Fetch user lookup when user logs in and is supervisor/admin
  useEffect(() => {
    const fetchUserLookup = async () => {
      if (user && (user.role === "supervisor" || user.is_admin)) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/lookup`);
          setUserLookup(response.data);
        } catch (error) {
          console.error("Failed to fetch user lookup:", error);
        }
      }
    };
    fetchUserLookup();
  }, [user]);

  // Update axios header when viewMode changes
  useEffect(() => {
    if (viewMode === "staff") {
      axios.defaults.headers.common["X-View-Mode"] = "staff";
    } else {
      delete axios.defaults.headers.common["X-View-Mode"];
    }
  }, [viewMode]);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["X-View-Mode"];
    setUser(null);
    setViewMode("default");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        {!user ? (
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/register" element={<Register />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
          <AppContent 
            user={user} 
            onLogout={handleLogout} 
            viewMode={viewMode} 
            setViewMode={setViewMode}
            userLookup={userLookup}
          />
        )}
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
